import React from 'react'

import { EditableImage } from 'features/views/attributes/editable/EditableImage'
import { EditableSection } from 'features/views/attributes/editable/section/EditableSection'
import { DetailViewHeaderEyebrowAttribute } from 'features/views/DetailView/attributes/DetailViewHeaderEyebrowAttribute'
import { DetailViewProfileImageAttribute } from 'features/views/DetailView/attributes/DetailViewProfileImageAttribute'
import { DetailViewSubtitleAttribute } from 'features/views/DetailView/attributes/DetailViewSubtitleAttribute'
import { DetailViewTitleAttribute } from 'features/views/DetailView/attributes/DetailViewTitleAttribute'
import { PrimaryHighlightsAttribute } from 'features/views/DetailView/attributes/primaryHighlights/PrimaryHighlightsAttribute'
import { DetailViewTopBar } from 'features/views/DetailView/headers/DetailViewTopBar'
import { DetailViewLayoutSectionStyle } from 'features/views/DetailView/layouts/DetailViewLayout.css'

import { Box } from 'ui/components/Box'
import { Skeleton } from 'ui/components/Skeleton'
import { useResponsiveValue } from 'ui/styling/helpers/useResponsiveValue'

import { useDetailViewCompactHeaderContentState } from './hooks/useDetailViewCompactHeaderContentState'
import { useDetailViewCompactHeaderCoverImageState } from './hooks/useDetailViewCompactHeaderCoverImageState'
import { useDetailViewCompactHeaderPrimaryHighlightsState } from './hooks/useDetailViewCompactHeaderPrimaryHighlightsState'
import { useDetailViewCompactHeaderProfileImageState } from './hooks/useDetailViewCompactHeaderProfileImageState'
import { useDetailViewCompactHeaderState } from './hooks/useDetailViewCompactHeaderState'
import { useDetailViewCompactHeaderTopBarState } from './hooks/useDetailViewCompactHeaderTopBarState'
import { DetailViewCompactHeaderActions } from './DetailViewCompactHeaderActions'

import {
    DetailViewCompactHeaderContentStyle,
    DetailViewCompactHeaderCoverImageStyles,
    DetailViewCompactHeaderProfileImageStyles,
    DetailViewCompactHeaderProfileImageWrapperStyles,
    DetailViewCompactHeaderStyle,
    DetailViewCompactHeaderSubtitleStyle,
    DetailViewCompactTopBarStyles,
} from './DetailViewCompactHeader.css'

type DetailViewCompactHeaderProps = {}

export const DetailViewCompactHeader: React.FC<DetailViewCompactHeaderProps> = () => {
    const { isLoading, topOffset, rightOffset, isError } = useDetailViewCompactHeaderState()

    return (
        <>
            <DetailViewCompactHeaderTopBar isLoading={isLoading} />
            {!isError && (
                <Box
                    as="header"
                    className={DetailViewCompactHeaderStyle}
                    style={{
                        top: topOffset,
                    }}
                >
                    <DetailViewCompactHeaderCoverImage isLoading={isLoading} />
                    <Box
                        flex
                        column
                        py={{ mobile: 'm', tablet: '4xl' }}
                        px={{ mobile: 'xl', tablet: '3xl' }}
                        shrink
                        grow
                        style={{
                            marginRight: rightOffset,
                        }}
                    >
                        <Box className={DetailViewLayoutSectionStyle}>
                            <DetailViewCompactHeaderContent isLoading={isLoading} />
                        </Box>
                    </Box>
                </Box>
            )}
        </>
    )
}

type DetailViewCompactHeaderContentProps = {
    isLoading?: boolean
}

const DetailViewCompactHeaderContent: React.FC<DetailViewCompactHeaderContentProps> = ({
    isLoading,
}) => {
    const { titleComponentSize, subtitleMaxLines } = useDetailViewCompactHeaderContentState()

    return (
        <Box className={DetailViewCompactHeaderContentStyle}>
            <DetailViewCompactHeaderProfileImage isLoading={isLoading} />
            <Box flex column shrink grow maxWidth="full">
                <Box flex center shrink gap="xl">
                    <DetailViewTitleAttribute
                        variant="headline"
                        size={titleComponentSize}
                        isLoading={isLoading}
                    />
                    <Box height="full" flex center noShrink>
                        <DetailViewHeaderEyebrowAttribute
                            size={{
                                mobile: 'xs',
                                tablet: 's',
                            }}
                            isLoading={isLoading}
                            noShrink
                        />
                    </Box>
                </Box>
                <Box
                    flex
                    column
                    shrink
                    style={{
                        maxWidth: '500px',
                    }}
                >
                    <DetailViewCompactHeaderPrimaryHighlights
                        isLoading={isLoading}
                        mt={{
                            mobile: 'l',
                            tablet: 'xs',
                        }}
                    />
                    <DetailViewSubtitleAttribute
                        isLoading={isLoading}
                        size="m"
                        mt="xs"
                        maxLines={subtitleMaxLines}
                        className={DetailViewCompactHeaderSubtitleStyle}
                    />
                </Box>
            </Box>
            <DetailViewCompactHeaderActions isLoading={isLoading} mt={{ mobile: 'm', tablet: 0 }} />
        </Box>
    )
}

type DetailViewCompactHeaderCoverImageProps = {
    isLoading?: boolean
    style?: React.CSSProperties
}

const DetailViewCompactHeaderCoverImage: React.FC<DetailViewCompactHeaderCoverImageProps> = (
    props
) => {
    const {
        coverType,
        backgroundColor,
        backgroundImageUrl,
        showOverlay,
        hasBackgroundImage,
        allowEditImage,
        editableImageType,
        onEditableImageFileChange,
        imageField,
    } = useDetailViewCompactHeaderCoverImageState()

    if (coverType === 'none') return null

    return (
        <Box
            className={DetailViewCompactHeaderCoverImageStyles.styleFunction({ coverType })}
            {...props}
        >
            <Box
                width="full"
                height="full"
                position="relative"
                overflow="hidden"
                style={{
                    backgroundColor,
                    backgroundImage: hasBackgroundImage ? `url(${backgroundImageUrl!})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    borderRadius: 'inherit',
                }}
            >
                {allowEditImage && (
                    <EditableImage
                        type={editableImageType}
                        field={imageField}
                        value={backgroundImageUrl}
                        size="4xl"
                        onChange={onEditableImageFileChange}
                    />
                )}
                {showOverlay && (
                    <Box
                        width="full"
                        height="full"
                        position="absolute"
                        top={0}
                        left={0}
                        style={{
                            background: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 1,
                        }}
                    />
                )}
            </Box>
        </Box>
    )
}

type DetailViewCompactHeaderTopBarProps = {
    isLoading?: boolean
}

const DetailViewCompactHeaderTopBar: React.FC<DetailViewCompactHeaderTopBarProps> = () => {
    const { headerRef, topOffset, coverType, shade } = useDetailViewCompactHeaderTopBarState()

    return (
        <DetailViewTopBar
            ref={headerRef}
            width="full"
            px={{ mobile: 'xl', tablet: '3xl' }}
            py={{ mobile: 'm', tablet: 'l' }}
            shade={shade}
            className={DetailViewCompactTopBarStyles.styleFunction({ coverType })}
            style={{
                top: topOffset,
            }}
        />
    )
}

type DetailViewCompactHeaderProfileImageProps = {
    isLoading?: boolean
}

const DetailViewCompactHeaderProfileImage: React.FC<DetailViewCompactHeaderProfileImageProps> = ({
    isLoading,
}) => {
    const { coverType, hasThumbnail } = useDetailViewCompactHeaderProfileImageState()

    const size = useResponsiveValue({
        mobile: 'xl',
        tablet: '2xl',
    })

    return (
        <Box
            className={DetailViewCompactHeaderProfileImageWrapperStyles.styleFunction({
                coverType,
            })}
        >
            {hasThumbnail && (
                <Box
                    className={DetailViewCompactHeaderProfileImageStyles.styleFunction({
                        coverType,
                    })}
                >
                    <Skeleton isLoading={isLoading}>
                        <DetailViewProfileImageAttribute
                            noShrink
                            isLoading={isLoading}
                            size={size}
                        />
                    </Skeleton>
                </Box>
            )}
        </Box>
    )
}

type DetailViewCompactHeaderPrimaryHighlightsProps = React.ComponentPropsWithoutRef<typeof Box> & {
    isLoading?: boolean
}

const DetailViewCompactHeaderPrimaryHighlights: React.FC<
    DetailViewCompactHeaderPrimaryHighlightsProps
> = ({ isLoading, ...props }) => {
    const { fields, showSeparators } = useDetailViewCompactHeaderPrimaryHighlightsState()

    if (!fields?.length) return null

    return (
        <EditableSection fields={fields} {...props}>
            <Box
                display="grid"
                style={{
                    gridTemplateColumns: `repeat(${fields.length}, minmax(10px, max-content))`,
                }}
                gap={{
                    mobile: showSeparators ? 'l' : 'xl',
                    tablet: showSeparators ? 'l' : '3xl',
                }}
                shrink
                grow
            >
                {fields?.map((field, idx) => {
                    const showSeparator = showSeparators && idx < fields.length - 1

                    return (
                        <PrimaryHighlightsAttribute
                            key={field.api_name}
                            field={field}
                            isLoading={isLoading}
                            size="m"
                            showSeparator={showSeparator}
                        />
                    )
                })}
            </Box>
        </EditableSection>
    )
}
